// Enunciado + envio por upload de arquivo.
//
// A equipe escreve o codigo no editor dela e sobe o arquivo. A linguagem sai
// da extensao; o navegador le o conteudo e manda no mesmo POST JSON de sempre,
// entao nao ha endpoint de upload no backend.

import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {
  ContestStateDto,
  Language,
  ProblemDetailDto,
  SubmissionListItemDto,
} from "@compila/shared";
import { api, ApiError } from "../../lib/api.js";
import { useCooldown } from "../../lib/hooks.js";
import {
  ACCEPT_ATTRIBUTE,
  byteSize,
  formatBytes,
  inspectFile,
  languageOf,
  LANGUAGE_LABEL,
  type FileProblem,
} from "../../lib/languages.js";
import { Button, ErrorBox, InfoBox, Spinner } from "../../components/ui.js";
import { formatMemory, formatTime } from "../../lib/format.js";

interface Arquivo {
  nome: string;
  conteudo: string;
  language: Language | null;
  problemas: FileProblem[];
}

export function TeamProblemPage(): React.JSX.Element {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<Arquivo | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  const cooldown = useCooldown();

  const problem = useQuery({
    queryKey: ["problem", id],
    queryFn: () => api.get<ProblemDetailDto>(`/problems/${id}`),
  });

  const contest = useQuery({
    queryKey: ["contest"],
    queryFn: () => api.get<ContestStateDto>("/contest"),
  });

  const enviar = useMutation({
    mutationFn: () => {
      if (!arquivo?.language) throw new Error("Selecione um arquivo valido");
      return api.post<SubmissionListItemDto>(`/problems/${id}/submissions`, {
        language: arquivo.language,
        sourceCode: arquivo.conteudo,
      });
    },
    onSuccess: async () => {
      setArquivo(null);
      if (inputRef.current) inputRef.current.value = "";
      cooldown.start(15);
      await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    },
    onError: (err) => {
      // O servidor e quem sabe quanto falta; o cliente so obedece.
      if (err instanceof ApiError && err.retryAfterSeconds !== undefined) {
        cooldown.start(err.retryAfterSeconds);
      }
    },
  });

  const lerArquivo = (file: File): void => {
    setErroLeitura(null);
    const reader = new FileReader();
    reader.onerror = () => setErroLeitura("Nao foi possivel ler o arquivo.");
    reader.onload = () => {
      const conteudo = String(reader.result ?? "");
      const language = languageOf(file.name);
      setArquivo({
        nome: file.name,
        conteudo,
        language,
        problemas: inspectFile(file.name, conteudo, language),
      });
      enviar.reset();
    };
    reader.readAsText(file);
  };

  if (problem.isPending) return <Spinner />;
  if (problem.error) {
    return <ErrorBox message="Problema nao encontrado ou ainda nao liberado." />;
  }

  const p = problem.data;
  const bloqueios = arquivo?.problemas.filter((x) => x.kind === "error") ?? [];
  const avisos = arquivo?.problemas.filter((x) => x.kind === "warning") ?? [];
  const contestParado = contest.data && contest.data.status !== "RUNNING";
  const podeEnviar =
    arquivo !== null &&
    bloqueios.length === 0 &&
    cooldown.remaining === 0 &&
    !enviar.isPending &&
    !contestParado;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* ---------------- Enunciado ---------------- */}
      <article className="rounded-lg border border-slate-200 bg-white p-6">
        <header className="mb-4 border-b border-slate-200 pb-4">
          <Link to="/problemas" className="text-sm text-sky-700 hover:underline">
            ← Todos os problemas
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">
            {p.label}. {p.title}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Tempo limite: {formatTime(p.timeLimitMs)} · Memoria:{" "}
            {formatMemory(p.memoryLimitKb)}
          </p>
        </header>

        <div className="prose prose-slate max-w-none prose-pre:bg-slate-900 prose-pre:text-slate-100">
          <Markdown remarkPlugins={[remarkGfm]}>{p.statementMd}</Markdown>
        </div>

        {p.samples.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 text-lg font-bold text-slate-900">Exemplos</h2>
            <div className="space-y-4">
              {p.samples.map((s) => (
                <div key={s.position} className="grid gap-3 sm:grid-cols-2">
                  <Exemplo titulo="Entrada" texto={s.stdin} />
                  <Exemplo titulo="Saida" texto={s.expectedOutput} />
                </div>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* ---------------- Envio ---------------- */}
      <aside className="lg:sticky lg:top-24 lg:self-start">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="mb-1 text-lg font-bold text-slate-900">Enviar solucao</h2>
          <p className="mb-4 text-xs text-slate-500">
            Suba o arquivo com seu codigo. A linguagem e identificada pela extensao.
          </p>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              const file = e.dataTransfer.files[0];
              if (file) lerArquivo(file);
            }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              arrastando
                ? "border-sky-500 bg-sky-50"
                : "border-slate-300 bg-slate-50 hover:border-slate-400"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) lerArquivo(file);
              }}
            />
            {arquivo ? (
              <div>
                <p className="font-mono text-sm font-semibold text-slate-900">
                  {arquivo.nome}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {arquivo.language ? LANGUAGE_LABEL[arquivo.language] : "linguagem desconhecida"}
                  {" · "}
                  {formatBytes(byteSize(arquivo.conteudo))}
                </p>
                <p className="mt-2 text-xs text-sky-700">Clique para trocar o arquivo</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Arraste o arquivo aqui
                </p>
                <p className="mt-1 text-xs text-slate-500">ou clique para escolher</p>
                <p className="mt-3 font-mono text-[11px] text-slate-400">
                  .py .java .c .cpp .js
                </p>
              </div>
            )}
          </div>

          {erroLeitura && (
            <div className="mt-3">
              <ErrorBox message={erroLeitura} />
            </div>
          )}

          {bloqueios.map((b, i) => (
            <div key={i} className="mt-3">
              <ErrorBox message={b.message} />
            </div>
          ))}

          {avisos.map((a, i) => (
            <div key={i} className="mt-3">
              <InfoBox tone="warning">{a.message}</InfoBox>
            </div>
          ))}

          {arquivo && bloqueios.length === 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-800">
                Conferir o conteudo do arquivo
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto rounded bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">
                <code>{arquivo.conteudo}</code>
              </pre>
            </details>
          )}

          {contestParado && (
            <div className="mt-3">
              <InfoBox tone="warning">
                {contest.data?.status === "PAUSED"
                  ? "A prova esta pausada. Aguarde a retomada para enviar."
                  : contest.data?.status === "ENDED"
                    ? "A prova terminou. Nao e mais possivel enviar."
                    : "A prova ainda nao comecou."}
              </InfoBox>
            </div>
          )}

          {enviar.error && !contestParado && (
            <div className="mt-3">
              <ErrorBox
                message={
                  enviar.error instanceof ApiError
                    ? enviar.error.message
                    : "Falha ao enviar. Tente de novo."
                }
              />
            </div>
          )}

          <Button
            onClick={() => enviar.mutate()}
            disabled={!podeEnviar}
            className="mt-4 w-full"
          >
            {enviar.isPending
              ? "Enviando..."
              : cooldown.remaining > 0
                ? `Aguarde ${cooldown.remaining}s`
                : "Enviar"}
          </Button>

          {enviar.isSuccess && cooldown.remaining > 0 && (
            <p className="mt-2 text-center text-xs text-emerald-700">
              Enviado. Acompanhe em{" "}
              <Link to="/submissoes" className="underline">
                Submissoes
              </Link>
              .
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

function Exemplo({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </div>
      <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
        <code>{texto}</code>
      </pre>
    </div>
  );
}
